/**
 * Endpoint de sincronización ERP — backend REAL.
 *
 * POST /api/erp-task/sync — dispara el sync de tareas desde ACSIS hacia
 * la tabla local `erp_task`. Devuelve stats (recibidas, total, duración).
 */

import { apiFetch } from '@/api/http';

export interface ErpTaskSyncResult {
  glnId: number;
  glnCode: string;
  erpType: string;
  recibidas: number;
  rowsAfectadas: number;
  sincronizadasAhora: number;
  totalEnDb: number;
  detalle: {
    /** Tareas para las que el GET del detalle terminó OK + partidas reemplazadas. */
    ok: number;
    /** Tareas que ya tenían `erp_response` poblado y se omitieron — los remitos
     *  son inmutables en ACSIS, no hace falta re-fetchear. */
    skipped: number;
    /** Tareas cuyo `claseComprobante` no matchea ningún endpoint conocido — se omiten. */
    sinEndpoint: number;
    /** Cantidad de tareas cuyo GET del detalle falló (timeout/4xx/5xx). El sync sigue. */
    errores: number;
    /** Total de partidas insertadas en `erp_task_detail` durante este sync. */
    totalPartidas: number;
    /** Tiempo total del subproceso de fetch del detalle (paralelo). */
    detalleDurationMs: number;
    /** Solo presente si `errores > 0`. Lista detallada para diagnóstico. */
    erroresLista?: Array<{ erpId: string; referencia: string; error: string }>;
  };
  /** Stats del seed/UPDATE de stock por el sync. */
  stock: {
    /** Series nuevas: INSERT en stock + eventos INGRESO + RESERVA. */
    insertadas: number;
    /** Series que ya estaban en stock en `a=1` o `na=1` (vinieron de un
     *  ingreso ANMAT previo): UPDATE a `r=1` + evento RESERVA con el
     *  delta del flag correspondiente. */
    reservadasDesdeStock: number;
    /** Series que ya estaban en `r=1` por otra tarea ERP — skip + log. */
    yaReservadas: number;
    /** Series que ya estaban en `o=1` (egresadas) — skip + log. Si vuelven
     *  por devolución, primero deben pasar por un INGRESO ANMAT que las
     *  vuelva a `a=1` antes de poder reservarse. */
    yaEgresadas: number;
    /** Series en `na` pendientes de reingreso físico (egreso anulado en
     *  tránsito): no se reservan hasta que vuelven. */
    pendientesReingreso?: number;
    /** Partidas con serie+sku pero cuyo GTIN no pudo resolverse en ACSIS. */
    sinGtin: number;
    /** Partidas sin numeroDeSerie o sin sku — no aplica a stock. */
    sinSerie: number;
  };
  syncStartedAt: string;
  syncFinishedAt: string;
  durationMs: number;
}

export async function syncErpTasks(glnId: string | number): Promise<ErpTaskSyncResult> {
  return apiFetch<ErpTaskSyncResult>('/erp-task/sync', {
    method: 'POST',
    body: { glnId: Number(glnId) },
    auth: true,
  });
}

/** Tarea local con shape camelCase. Espejo del response del back. */
export interface ErpTask {
  id: number;
  glnId: number;
  estado: string;
  fTraza: string | null;
  fInformadoErp: string | null;
  lastSyncDate: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  erp: {
    id: string | null;
    tipo: string | null;
    nombre: string | null;
    fechaCreacion: string | null;
    asunto: string | null;
    descripcion: string | null;
    prioridad: string | null;
    solicitante: string | null;
    nombreSolicitante: string | null;
    responsable: string | null;
    nombreResponsable: string | null;
    comentariosSolicitante: string | null;
    fechaVencimiento: string | null;
    referencia: string | null;
    fechaAsignacion: string | null;
    fechaLectura: string | null;
    fechaInicio: string | null;
    fechaCumplimiento: string | null;
    fechaRevisionCumplimiento: string | null;
    comentarioCumplimiento: string | null;
    claseEntidad: string | null;
    claseComprobante: string | null;
    entidad: string | null;
    renglonComprobante: string | null;
    realizada: string | null;
    clasificador1: string | null;
    clasificador2: string | null;
    clasificador3: string | null;
    clasificador4: string | null;
    clasificador5: string | null;
    clasificador6: string | null;
    clasificador7: string | null;
    clasificador8: string | null;
  };
}

export interface ListErpTasksResponse {
  tareas: ErpTask[];
  glnId: number;
  estado: string;
  limit: number;
  offset: number;
}

export interface ListErpTasksFilters {
  /** Filtra por estado exacto. 'all' o omitir → todas. Default backend: 'pendiente'. */
  estado?: string;
  limit?: number;
  offset?: number;
}

export async function listErpTasks(
  glnId: string | number,
  filters: ListErpTasksFilters = {},
): Promise<ListErpTasksResponse> {
  const params = new URLSearchParams({ glnId: String(Number(glnId)) });
  if (filters.estado) params.set('estado', filters.estado);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.offset) params.set('offset', String(filters.offset));
  return apiFetch<ListErpTasksResponse>(`/erp-task?${params.toString()}`, { auth: true });
}

/** Último sync exitoso registrado para el GLN — leído de audit_log. */
export interface LastSyncResponse {
  glnId: number;
  /** ISO timestamp del audit. null si nunca se sincronizó este GLN. */
  at: string | null;
  /** Metadata del audit (recibidas, totalEnDb, durationMs, etc.). null si no hay. */
  metadata: Record<string, unknown> | null;
}

export async function getLastErpTaskSync(glnId: string | number): Promise<LastSyncResponse> {
  return apiFetch<LastSyncResponse>(
    `/erp-task/last-sync?glnId=${encodeURIComponent(String(Number(glnId)))}`,
    { auth: true },
  );
}


// =============================================================================
// Ingesta manual de un JSON de detalle (pruebas del pickeo)
// =============================================================================

/** GET /api/erp-task/ingesta-manual — si el back tiene ERP_INGESTA_MANUAL=true. */
export interface IngestaManualEstado {
  habilitada: boolean;
}

export async function getIngestaManual(): Promise<IngestaManualEstado> {
  return apiFetch<IngestaManualEstado>('/erp-task/ingesta-manual', { auth: true });
}

/** Resultado de la ingesta: la tarea de prueba creada con el JSON pegado. */
export interface IngestaManualResult {
  referencia: string;
  /** Id ficticio de la tarea (`PRUEBA-…`): no existe en Plataforma. */
  erpId: string;
  taskId: number;
  partidas: number;
  stock: ErpTaskSyncResult['stock'];
}

/**
 * POST /api/erp-task/ingesta-manual — crea una tarea de prueba con el detalle
 * pegado (mismo JSON que devuelve ACSIS), sin pedir nada a Plataforma.
 */
export async function ingestarDetalleManual(
  glnId: string | number,
  detalle: unknown,
): Promise<IngestaManualResult> {
  return apiFetch<IngestaManualResult>('/erp-task/ingesta-manual', {
    method: 'POST',
    body: { glnId: Number(glnId), detalle },
    auth: true,
  });
}
